
import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Users, Mail, Search } from 'lucide-react';
import { generateMembers } from './communityData';
import { useIsMobile } from '@/hooks/use-mobile';
import TranslatedText from '@/TranslatedText';

const members = generateMembers();

const MembersTab: React.FC = () => {
  const isMobile = useIsMobile();
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-bold text-base">
          <TranslatedText text="Community Members" />
        </h2>
        <div className="relative w-48">
          <Input 
            placeholder="Search members..." 
            className="pr-8 text-xs h-8" 
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Search className="h-3 w-3 text-gray-400" />
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {members.map(member => (
          <motion.div 
            key={member.id} 
            initial={{opacity: 0, scale: 0.95}} 
            animate={{opacity: 1, scale: 1}} 
            transition={{duration: 0.4}}
          >
            <Card className="hover:shadow-md transition-shadow">
              <CardContent className="pt-4">
                <div className="flex flex-col items-center text-center mb-3">
                  <Avatar className="h-16 w-16 mb-2">
                    <AvatarImage src={member.avatar} alt={member.name} />
                    <AvatarFallback>{member.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <h3 className="font-semibold text-sm">{member.name}</h3>
                  <Badge className="mt-1 bg-gradient-to-r from-blue-500 to-purple-600 text-xs px-2 py-0">
                    {member.role}
                  </Badge>
                  <p className="text-xs text-gray-500 mt-1">
                    <TranslatedText text="Joined" /> {member.joined}
                  </p>
                </div>
                
                <div className="flex justify-center gap-4 my-2">
                  <div className="text-center">
                    <p className="text-sm font-semibold">{member.posts}</p>
                    <p className="text-xs text-gray-500">
                      <TranslatedText text="Posts" />
                    </p>
                  </div>
                  <Separator orientation="vertical" />
                  <div className="text-center">
                    <p className="text-sm font-semibold">{member.likes}</p>
                    <p className="text-xs text-gray-500">
                      <TranslatedText text="Likes" />
                    </p>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-center border-t pt-3 pb-2">
                <Button variant="outline" size="sm" className="h-6 text-xs mr-1" aria-label="Message">
                  <Mail className="h-3 w-3" />
                  {!isMobile && <span className="ml-1"><TranslatedText text="Message" /></span>}
                </Button>
                <Button size="sm" className="h-6 text-xs" aria-label="Connect">
                  <Users className="h-3 w-3" />
                  {!isMobile && <span className="ml-1"><TranslatedText text="Connect" /></span>}
                </Button>
              </CardFooter>
            </Card>
          </motion.div>
        ))}
      </div>
      
      <div className="flex justify-center">
        <Button variant="outline" className="mt-2 text-xs">
          <TranslatedText text="View All Members" />
        </Button>
      </div>
    </div>
  );
};

export default MembersTab;
